/**
 * Integer minor-unit money arithmetic (ARCHITECTURE §4, goal #5).
 *
 * Every amount is an integer count of minor units. All arithmetic is integer.
 * Floats appear only at the display/parse edge (format / parseAmount), never in
 * a stored value. This is what makes tie-out exact and rounding drift impossible.
 */
import type { Currency, Money } from "./types";

/** Minor digits per currency. All v1 currencies use 2. */
const MINOR_DIGITS: Record<Currency, number> = {
  AED: 2,
  USD: 2,
  EUR: 2,
  GBP: 2,
};

export function minorDigits(currency: Currency): number {
  return MINOR_DIGITS[currency];
}

/** Construct money from an integer minor-unit amount. */
export function money(amount: number, currency: Currency): Money {
  if (!Number.isInteger(amount)) {
    throw new Error(`Money amount must be an integer minor unit, got ${amount}`);
  }
  return { amount, currency };
}

/** Zero in a currency. */
export function zero(currency: Currency): Money {
  return { amount: 0, currency };
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new Error(`Currency mismatch: ${a.currency} vs ${b.currency}`);
  }
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amount: a.amount + b.amount, currency: a.currency };
}

export function subtract(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amount: a.amount - b.amount, currency: a.currency };
}

export function negate(a: Money): Money {
  return { amount: -a.amount, currency: a.currency };
}

export function isZero(a: Money): boolean {
  return a.amount === 0;
}

export function isNegative(a: Money): boolean {
  return a.amount < 0;
}

export function abs(a: Money): Money {
  return { amount: Math.abs(a.amount), currency: a.currency };
}

export function compare(a: Money, b: Money): number {
  assertSameCurrency(a, b);
  return a.amount - b.amount;
}

/**
 * Penny-safe split of an amount into `weights.length` parts proportional to
 * `weights`, distributing any rounding remainder one minor unit at a time so
 * the parts always sum back to the original. Guarantees no drift.
 */
export function allocate(a: Money, weights: readonly number[]): Money[] {
  const total = weights.reduce((s, w) => s + w, 0);
  if (total <= 0) throw new Error("allocate weights must sum to a positive number");
  const parts: number[] = [];
  let allocated = 0;
  for (const w of weights) {
    const part = Math.floor((a.amount * w) / total);
    parts.push(part);
    allocated += part;
  }
  let remainder = a.amount - allocated;
  // Hand out remaining minor units to the earliest parts.
  for (let i = 0; remainder !== 0 && i < parts.length; i += 1) {
    const step = remainder > 0 ? 1 : -1;
    parts[i] = (parts[i] as number) + step;
    remainder -= step;
  }
  return parts.map((p) => ({ amount: p, currency: a.currency }));
}

/**
 * Multiply an amount by a decimal rate and round half-up to the nearest minor
 * unit. Used by VAT/CT/FX helpers; result is still an exact integer of minor
 * units.
 */
export function scale(a: Money, rate: number): Money {
  const raw = a.amount * rate;
  return { amount: Math.round(raw), currency: a.currency };
}

/**
 * Convert a foreign amount to a target currency at `rate` (target units per
 * source unit), rounding to the nearest target minor unit.
 */
export function convert(a: Money, target: Currency, rate: number): Money {
  return { amount: Math.round(a.amount * rate), currency: target };
}

/**
 * Parse a human-typed decimal string ("44,076.00", "(4,120.00)", "-3.5") into
 * money. Accepts thousands separators and parenthesised negatives. This is the
 * ONLY place floats touch the domain — the result is an integer of minor units.
 */
export function parseAmount(input: string, currency: Currency): Money {
  const trimmed = input.trim();
  if (trimmed === "") return zero(currency);
  const negative = /^\(.*\)$/.test(trimmed) || trimmed.startsWith("-");
  const digits = trimmed.replace(/[(),\s]/g, "").replace(/^-/, "");
  if (digits !== "" && !/^\d*\.?\d*$/.test(digits)) {
    throw new Error(`Not a valid amount: "${input}"`);
  }
  const value = Number(digits === "" || digits === "." ? "0" : digits);
  if (!Number.isFinite(value)) throw new Error(`Not a valid amount: "${input}"`);
  const factor = 10 ** minorDigits(currency);
  const minor = Math.round(value * factor);
  return { amount: negative ? -minor : minor, currency };
}

/**
 * Format an amount as a grouped decimal string ("1,284,905.00"). Negatives are
 * parenthesised by default (accounting convention). Locale drives grouping.
 */
export function format(
  a: Money,
  locale = "en-AE",
  options: { parenNegative?: boolean } = {},
): string {
  const { parenNegative = true } = options;
  const digits = minorDigits(a.currency);
  const factor = 10 ** digits;
  const value = Math.abs(a.amount) / factor;
  const body = value.toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  if (a.amount < 0) return parenNegative ? `(${body})` : `-${body}`;
  return body;
}

/** Format with the currency code appended, e.g. "AED 1,284,905.00". */
export function formatWithCurrency(a: Money, locale = "en-AE"): string {
  return `${a.currency} ${format(a, locale)}`;
}
